import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { Activity, Users, Eye, Target, ExternalLink } from "lucide-react";
import { useSyncedRange, RANGE_HEADING } from "../../lib/useSyncedRange";
import RangeToggle from "./RangeToggle";

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(Math.round(n));
}

function pctDelta(cur, prev) {
  if (prev == null || prev === 0) return null;
  return ((cur - prev) / prev) * 100;
}

function DeltaChip({ value }) {
  if (value == null) return <span className="text-[10px] text-[#a0aec0] font-mono-tight">—</span>;
  const positive = value >= 0;
  const color = positive ? "#48bb78" : "#f56565";
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-md font-mono-tight"
      style={{ background: color + "1a", color }}
    >
      {positive ? "▲" : "▼"} {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function KpiCard({ icon: Icon, label, current, previous, sparkData, sparkKey, prevLabel, testid }) {
  const delta = pctDelta(current, previous);
  return (
    <div className="card-soft p-5 flex flex-col gap-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center">
            <Icon className="h-4 w-4" strokeWidth={2.4} />
          </span>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">{label}</div>
        </div>
        <DeltaChip value={delta} />
      </div>
      <div>
        <div className="font-mono-tight text-2xl text-[#1a202c]">{fmtNum(current)}</div>
        <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-0.5">
          vs {fmtNum(previous)} {prevLabel || "prior 28d"}
        </div>
      </div>
      <div className="h-10 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={sparkData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <Tooltip
              contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 11, padding: "4px 8px" }}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ""}
              formatter={(v) => [fmtNum(v), label]}
            />
            <Line type="monotone" dataKey={sparkKey} stroke="#4fd1c5" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TopPagesCard({ rows, heading }) {
  return (
    <div className="card-soft overflow-hidden" data-testid="ga4-top-pages-card">
      <div className="px-5 py-4 border-b border-[#edf2f7] flex items-start gap-3">
        <span className="h-9 w-9 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
          <ExternalLink className="h-4 w-4" />
        </span>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Organic search · {heading}</div>
          <h3 className="font-display text-base mt-0.5 text-[#1a202c]">Top landing pages</h3>
        </div>
      </div>
      {(rows || []).length === 0 ? (
        <div className="p-6 text-center text-xs text-[#a0aec0] font-mono-tight">
          No organic search sessions recorded in the {heading}.
        </div>
      ) : (
        <div className="grid gap-x-4 px-5 py-3 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]"
             style={{ gridTemplateColumns: "1fr 80px 80px 80px" }}>
          <div>Landing page</div>
          <div className="text-right">Sessions</div>
          <div className="text-right">Engaged</div>
          <div className="text-right">Conversions</div>
        </div>
      )}
      <div className="divide-y divide-[#edf2f7] max-h-[420px] overflow-y-auto">
        {(rows || []).map((r, i) => (
          <div
            key={r.landing_page + i}
            data-testid={`ga4-page-row-${i}`}
            className="grid gap-x-4 px-5 py-2.5 hover:bg-[#f7fafc] text-sm items-center"
            style={{ gridTemplateColumns: "1fr 80px 80px 80px" }}
          >
            <div className="truncate text-[#1a202c] font-mono-tight text-xs" title={r.landing_page}>
              {r.landing_page}
            </div>
            <div className="text-right font-mono-tight text-[#4a5568]">{fmtNum(r.sessions)}</div>
            <div className="text-right font-mono-tight text-[#4a5568]">{fmtNum(r.engaged_sessions)}</div>
            <div className="text-right font-mono-tight text-[#4a5568]">{fmtNum(r.conversions)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const RANGE_HEADING_FALLBACK = "last 30 days";

export default function GA4Panel({ client }) {
  const [data, setData] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [range, setRange] = useSyncedRange();

  const propertyId = client?.ga4_property_id;

  useEffect(() => {
    let alive = true;
    if (!propertyId) return;
    (async () => {
      if (alive) {
        setLoading(true);
        setError(null);
      }
      try {
        const [ov, tp] = await Promise.all([
          api.get(`/clients/${client.id}/ga4/overview?range=${range}`),
          api.get(`/clients/${client.id}/ga4/top-pages?limit=10&range=${range}`),
        ]);
        if (!alive) return;
        setData(ov.data);
        setPages(tp.data?.rows || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed to load GA4 data");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [client.id, propertyId, range]);

  if (!propertyId) return null;

  if (loading && !data) {
    return (
      <div className="card-soft p-6 text-sm text-[#a0aec0] font-mono-tight" data-testid="ga4-loading">
        Fetching Google Analytics…
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-soft p-5 border border-[#feb2b2] bg-[#fed7d7]/30" data-testid="ga4-error">
        <div className="text-[11px] uppercase tracking-widest text-[#742a2a] font-bold">GA4 error</div>
        <div className="text-xs text-[#742a2a] mt-1 font-mono-tight">{error}</div>
      </div>
    );
  }

  if (!data?.configured) return null;

  const cur = data.current || {};
  const prev = data.previous || {};
  const spark = data.daily || [];
  const prevLabel = data.range_label || "prior period";
  const heading = RANGE_HEADING[range] || RANGE_HEADING_FALLBACK;

  return (
    <div className="space-y-5" data-testid="ga4-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
            Google Analytics 4 · {heading}
          </div>
          <h3 className="font-display text-lg mt-0.5 text-[#1a202c]">Traffic overview</h3>
        </div>
        <div className="flex items-center gap-3">
          <RangeToggle range={range} onChange={setRange} disabled={loading} testid="ga4-range-toggle" />
          <div className="text-[10px] text-[#a0aec0] font-mono-tight uppercase tracking-widest">
            property {data.property_id}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <KpiCard icon={Activity} label="Sessions" current={cur.sessions} previous={prev.sessions} sparkData={spark} sparkKey="sessions" prevLabel={prevLabel} testid="ga4-kpi-sessions" />
        <KpiCard icon={Users} label="Users" current={cur.users} previous={prev.users} sparkData={spark} sparkKey="users" prevLabel={prevLabel} testid="ga4-kpi-users" />
        <KpiCard icon={Eye} label="Engaged sessions" current={cur.engaged_sessions} previous={prev.engaged_sessions} sparkData={spark} sparkKey="engaged_sessions" prevLabel={prevLabel} testid="ga4-kpi-engaged" />
        <KpiCard icon={Target} label="Conversions" current={cur.conversions} previous={prev.conversions} sparkData={spark} sparkKey="conversions" prevLabel={prevLabel} testid="ga4-kpi-conversions" />
      </div>

      <TopPagesCard rows={pages} heading={heading} />
    </div>
  );
}
